/**
 * Row validation, driven by the accepted contract itself.
 *
 * There is no hand-written mirror of `BUSINESS_SCHEMA_v1.json` here, and that is the point: a
 * second copy of a contract is a second thing to keep true, and the one that drifts is always the
 * copy. This walks the document, so adding a field to the schema adds it to the validator, and a
 * row that validates here validates against the contract the founder accepted.
 *
 * It implements the subset of JSON Schema the document actually uses — `type` (including a list of
 * types), `enum`, `const`, `required`, `properties`, `items`, `minimum`, `maximum`, `pattern` — and
 * throws on a keyword it does not know rather than quietly ignoring it. An unknown keyword means
 * the contract has grown a rule this validator is not enforcing, which is exactly the failure this
 * design exists to prevent.
 */
import schema from "../generated/business_schema_v1.json" with { type: "json" };

export const BUSINESS_SCHEMA = schema as unknown as JsonSchema;
export const SCHEMA_VERSION: string = (schema as { version: string }).version;

export interface JsonSchema {
  type?: string | string[];
  enum?: unknown[];
  const?: unknown;
  required?: string[];
  properties?: Record<string, JsonSchema>;
  items?: JsonSchema;
  minimum?: number;
  maximum?: number;
  pattern?: string;
  [keyword: string]: unknown;
}

/** Keywords that describe rather than constrain, and are correctly ignored. */
const ANNOTATIONS = new Set([
  "$schema", "title", "description", "default", "format", "version", "status", "ledger_ref",
  // Added by the generator that copies the accepted document into this package.
  "_generated",
]);
const SUPPORTED = new Set([
  "type", "enum", "const", "required", "properties", "items", "minimum", "maximum", "pattern",
]);

export interface Violation {
  /** Dotted path to the offending value, e.g. `operations.hours_by_process[0].basis`. */
  path: string;
  message: string;
}

function typeOf(value: unknown): string {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  if (Number.isInteger(value)) return "integer";
  return typeof value;
}

function typeMatches(value: unknown, expected: string): boolean {
  const actual = typeOf(value);
  if (expected === "number") return actual === "number" || actual === "integer";
  return actual === expected;
}

function check(value: unknown, node: JsonSchema, path: string, out: Violation[]): void {
  for (const keyword of Object.keys(node)) {
    if (!SUPPORTED.has(keyword) && !ANNOTATIONS.has(keyword)) {
      throw new Error(`BUSINESS_SCHEMA uses ${keyword} at ${path || "root"}, which this validator does not enforce`);
    }
  }

  if (node.const !== undefined && value !== node.const) {
    out.push({ path, message: `must be ${JSON.stringify(node.const)}, got ${JSON.stringify(value)}` });
    return;
  }

  if (node.enum !== undefined) {
    if (!node.enum.some((allowed) => allowed === value)) {
      out.push({ path, message: `${JSON.stringify(value)} is not one of ${JSON.stringify(node.enum)}` });
    }
    return;
  }

  if (node.type !== undefined) {
    const allowed = Array.isArray(node.type) ? node.type : [node.type];
    if (!allowed.some((t) => typeMatches(value, t))) {
      out.push({ path, message: `expected ${allowed.join(" or ")}, got ${typeOf(value)}` });
      return;
    }
  }

  if (typeof value === "number") {
    if (node.minimum !== undefined && value < node.minimum) {
      out.push({ path, message: `${value} is below the minimum ${node.minimum}` });
    }
    if (node.maximum !== undefined && value > node.maximum) {
      out.push({ path, message: `${value} is above the maximum ${node.maximum}` });
    }
  }

  if (typeof value === "string" && node.pattern !== undefined) {
    if (!new RegExp(node.pattern).test(value)) {
      out.push({ path, message: `${JSON.stringify(value)} does not match ${node.pattern}` });
    }
  }

  if (Array.isArray(value) && node.items !== undefined) {
    value.forEach((item, i) => check(item, node.items as JsonSchema, `${path}[${i}]`, out));
  }

  if (value !== null && typeof value === "object" && !Array.isArray(value)) {
    const record = value as Record<string, unknown>;
    for (const name of node.required ?? []) {
      if (!(name in record)) {
        out.push({ path: path ? `${path}.${name}` : name, message: "is required and absent" });
      }
    }
    for (const [name, child] of Object.entries(node.properties ?? {})) {
      if (name in record) check(record[name], child, path ? `${path}.${name}` : name, out);
    }
    // A field the contract does not define is not rejected — enrichment and future blocks are
    // additive — but it is reported, because a typo in a field name is silent data loss.
    for (const name of Object.keys(record)) {
      if (node.properties && !(name in node.properties)) {
        out.push({ path: path ? `${path}.${name}` : name, message: "is not a field the schema defines" });
      }
    }
  }
}

export interface ValidationResult { valid: boolean; violations: Violation[] }

/** Validate a row against the accepted contract. Total: it reports every violation, not the first. */
export function validateRow(row: unknown, against: JsonSchema = BUSINESS_SCHEMA): ValidationResult {
  const violations: Violation[] = [];
  check(row, against, "", violations);
  return { valid: violations.length === 0, violations };
}
